import type {
  BaseTokenInfo,
  SupportedChainName,
  TokenDeployment,
  TokenInfo,
  TokenValue,
} from "@src/components/DefuseSDK/types/base"
import { assert } from "@src/components/DefuseSDK/utils/assert"
import { isHyperliquid } from "@src/components/DefuseSDK/utils/hyperliquid"
import { isBaseToken } from "@src/components/DefuseSDK/utils/token"
import { resolveTokenOut } from "@src/components/DefuseSDK/utils/tokenRouting"
import { validateAddress } from "@src/components/DefuseSDK/utils/validateAddress"
import { LIST_TOKENS_FLATTEN, tokenFamilies } from "@src/constants/tokens"
import { parseNearcomAddress } from "@src/shared/utils/nearcomAddress"
import { isNearIntentsNetwork } from "@src/shared/utils/networks"
import { type ActorRef, fromTransition, type Snapshot } from "xstate"
import { requiresExchangeFundsAck } from "../utils/cexCompatibility"
import {
  type ParsedWithdrawRecipient,
  parsedWithdrawRecipientsEqual,
} from "../utils/withdrawRecipient"

export type Fields = Array<Exclude<keyof State, "parentRef">>
const fields: Fields = [
  "tokenIn",
  "paymentToken",
  "tokenOut",
  "parsedAmount",
  "parsedRecipient",
  "parsedDestinationMemo",
  "cexFundsLooseConfirmation",
]

export type ParentEvents = {
  type: "WITHDRAW_FORM_FIELDS_CHANGED"
  fields: Fields
}

type ParentActor = ActorRef<Snapshot<unknown>, ParentEvents>

type CexFundsLooseConfirmationStatus =
  | "confirmed"
  | "not_confirmed"
  | "not_required"

// XState v5's Actor._send() uses JSON.stringify for dev inspection, which fails with BigInt.
type SerializableTokenValue = { amount: string; decimals: number }

export type Events =
  | {
      type: "WITHDRAW_FORM.UPDATE_TOKEN"
      params: {
        token: TokenInfo
        parsedAmount: SerializableTokenValue | null
      }
    }
  | {
      type: "WITHDRAW_FORM.UPDATE_BLOCKCHAIN"
      params: {
        blockchain: SupportedChainName | "near_intents"
      }
    }
  | {
      type: "WITHDRAW_FORM.UPDATE_BLOCKCHAIN_AND_RECIPIENT"
      params: {
        blockchain: SupportedChainName | "near_intents"
        recipient: string
        proxyRecipient?: string | null
      }
    }
  | {
      type: "WITHDRAW_FORM.UPDATE_AMOUNT"
      params: {
        amount: string
        parsedAmount: SerializableTokenValue | null
        notifyParent?: boolean
      }
    }
  | {
      type: "WITHDRAW_FORM.RECIPIENT"
      params: {
        recipient: string
        proxyRecipient: string | null
      }
    }
  | {
      type: "WITHDRAW_FORM.UPDATE_DESTINATION_MEMO"
      params: {
        destinationMemo: string
      }
    }
  | {
      type: "WITHDRAW_FORM.CEX_FUNDS_LOOSE_CHANGED"
      params: {
        cexFundsLooseConfirmation: CexFundsLooseConfirmationStatus
      }
    }
  | {
      type: "WITHDRAW_FORM.UPDATE_MIN_RECEIVED_AMOUNT"
      params: {
        minReceivedAmount: TokenValue | null
      }
    }
  | {
      type: "WITHDRAW_FORM.UPDATE_PAYMENT_TOKEN"
      params: {
        paymentToken: TokenInfo
      }
    }
  | {
      type: "WITHDRAW_FORM.PICK_PAYMENT_TOKEN"
      params: {
        paymentToken: TokenInfo
      }
    }

export type State = {
  parentRef: ParentActor
  tokenIn: TokenInfo
  paymentToken: TokenInfo
  paymentTokenCustomized: boolean
  tokenOut: BaseTokenInfo
  tokenOutDeployment: TokenDeployment
  amount: string
  parsedAmount: TokenValue | null
  recipient: string
  parsedRecipient: ParsedWithdrawRecipient | null
  destinationMemo: string
  parsedDestinationMemo: string | null
  cexFundsLooseConfirmation: CexFundsLooseConfirmationStatus
  minReceivedAmount: TokenValue | null
  blockchain: SupportedChainName | "near_intents"
}

export const withdrawFormReducer = fromTransition(
  (state, event: Events) => {
    let newState = state
    const eventType = event.type
    switch (eventType) {
      case "WITHDRAW_FORM.UPDATE_TOKEN": {
        // Keep the pin when blockchain is near_intents; otherwise the
        // first deployment of a unified token would silently override it.
        const isIntentsNetwork = isNearIntentsNetwork(state.blockchain)
        const [tokenOut, tokenOutDeployment] = isIntentsNetwork
          ? resolveTokenOut(
              state.blockchain,
              event.params.token,
              tokenFamilies,
              LIST_TOKENS_FLATTEN
            )
          : fallbackTokenOut(
              event.params.token,
              state.tokenOutDeployment.chainName
            )

        const blockchain = isIntentsNetwork
          ? state.blockchain
          : tokenOutDeployment.chainName

        const parsedAmount = event.params.parsedAmount
          ? {
              amount: BigInt(event.params.parsedAmount.amount),
              decimals: event.params.parsedAmount.decimals,
            }
          : null

        newState = {
          ...state,
          amount: "",
          parsedAmount,
          tokenIn: event.params.token,
          paymentToken: event.params.token,
          paymentTokenCustomized: false,
          tokenOut,
          tokenOutDeployment,
          recipient: "",
          parsedRecipient: null,
          destinationMemo: "",
          parsedDestinationMemo: null,
          cexFundsLooseConfirmation: isIntentsNetwork
            ? "not_required"
            : cexFundsLooseConfirmationStatusDefault(tokenOutDeployment),
          minReceivedAmount: null,
          blockchain,
        }
        break
      }
      case "WITHDRAW_FORM.UPDATE_BLOCKCHAIN": {
        const blockchain = event.params.blockchain

        const [tokenOut, tokenOutDeployment] = resolveTokenOut(
          blockchain,
          state.tokenIn,
          tokenFamilies,
          LIST_TOKENS_FLATTEN
        )

        const cexFundsLooseConfirmation = isNearIntentsNetwork(blockchain)
          ? "not_required"
          : cexFundsLooseConfirmationStatusDefault(tokenOutDeployment)

        newState = {
          ...state,
          tokenOut,
          tokenOutDeployment,
          recipient: "",
          parsedRecipient: null,
          destinationMemo: "",
          parsedDestinationMemo: null,
          cexFundsLooseConfirmation,
          minReceivedAmount: null,
          blockchain,
        }
        break
      }
      case "WITHDRAW_FORM.UPDATE_BLOCKCHAIN_AND_RECIPIENT": {
        const { blockchain, recipient, proxyRecipient } = event.params

        const [tokenOut, tokenOutDeployment] = resolveTokenOut(
          blockchain,
          state.tokenIn,
          tokenFamilies,
          LIST_TOKENS_FLATTEN
        )

        const cexFundsLooseConfirmation = isNearIntentsNetwork(blockchain)
          ? "not_required"
          : cexFundsLooseConfirmationStatusDefault(tokenOutDeployment)

        const determinedRecipient =
          isHyperliquid(blockchain) &&
          tokenOutDeployment.chainName !== "hyperliquid"
            ? (proxyRecipient ?? null)
            : recipient

        const parsedRecipientResult =
          determinedRecipient != null
            ? parseRecipient(
                determinedRecipient,
                tokenOutDeployment,
                isNearIntentsNetwork(blockchain)
              )
            : null
        const parsedRecipient = parsedWithdrawRecipientsEqual(
          state.parsedRecipient,
          parsedRecipientResult
        )
          ? state.parsedRecipient
          : parsedRecipientResult
        const isInternal = isNearIntentsNetwork(blockchain)

        newState = {
          ...state,
          tokenOut,
          tokenOutDeployment,
          recipient:
            isInternal && parsedRecipient?.kind === "nearcom"
              ? parsedRecipient.address.displayAddress
              : recipient,
          parsedRecipient,
          destinationMemo: "",
          parsedDestinationMemo: null,
          cexFundsLooseConfirmation,
          minReceivedAmount: null,
          blockchain,
        }
        break
      }
      case "WITHDRAW_FORM.UPDATE_AMOUNT": {
        const parsedAmount = event.params.parsedAmount
          ? {
              amount: BigInt(event.params.parsedAmount.amount),
              decimals: event.params.parsedAmount.decimals,
            }
          : null

        newState = {
          ...state,
          parsedAmount,
          amount: event.params.amount,
        }
        break
      }
      case "WITHDRAW_FORM.RECIPIENT": {
        const recipient = event.params.recipient
        const determinedRecipient =
          isHyperliquid(state.blockchain) &&
          state.tokenOutDeployment.chainName !== "hyperliquid"
            ? event.params.proxyRecipient
            : event.params.recipient

        const parsedRecipientResult = determinedRecipient
          ? parseRecipient(
              determinedRecipient,
              state.tokenOutDeployment,
              isNearIntentsNetwork(state.blockchain)
            )
          : null
        const parsedRecipient = parsedWithdrawRecipientsEqual(
          state.parsedRecipient,
          parsedRecipientResult
        )
          ? state.parsedRecipient
          : parsedRecipientResult
        const isInternal = isNearIntentsNetwork(state.blockchain)

        newState = {
          ...state,
          recipient:
            isInternal && parsedRecipient?.kind === "nearcom"
              ? parsedRecipient.address.displayAddress
              : recipient,
          parsedRecipient,
        }
        break
      }
      case "WITHDRAW_FORM.UPDATE_DESTINATION_MEMO": {
        newState = {
          ...state,
          destinationMemo: event.params.destinationMemo,
          parsedDestinationMemo: parseDestinationMemo(
            event.params.destinationMemo,
            state.tokenOutDeployment.chainName
          ),
        }
        break
      }
      case "WITHDRAW_FORM.CEX_FUNDS_LOOSE_CHANGED": {
        newState = {
          ...state,
          cexFundsLooseConfirmation: event.params.cexFundsLooseConfirmation,
        }
        break
      }
      case "WITHDRAW_FORM.UPDATE_MIN_RECEIVED_AMOUNT": {
        newState = {
          ...state,
          minReceivedAmount: event.params.minReceivedAmount,
        }
        break
      }
      case "WITHDRAW_FORM.UPDATE_PAYMENT_TOKEN": {
        newState = {
          ...state,
          paymentToken: event.params.paymentToken,
          paymentTokenCustomized: false,
        }
        break
      }
      case "WITHDRAW_FORM.PICK_PAYMENT_TOKEN": {
        newState = {
          ...state,
          paymentToken: event.params.paymentToken,
          paymentTokenCustomized: true,
        }
        break
      }
      default: {
        event satisfies never
        return state
      }
    }

    const changedFields: Fields = []
    for (const key of fields) {
      if (newState[key] !== state[key]) {
        changedFields.push(key)
      }
    }
    const shouldNotifyParent =
      event.type !== "WITHDRAW_FORM.UPDATE_AMOUNT" ||
      event.params.notifyParent !== false

    if (shouldNotifyParent && changedFields.length > 0) {
      state.parentRef.send({
        type: "WITHDRAW_FORM_FIELDS_CHANGED",
        fields: changedFields,
      })
    }

    return newState
  },
  ({
    input,
  }: {
    input: {
      parentRef: ParentActor
      tokenIn: TokenInfo
      paymentToken?: TokenInfo
      amount?: string
      parsedAmount?: SerializableTokenValue | null
      blockchain?: SupportedChainName | "near_intents"
      recipient?: string
    }
  }): State => {
    const [tokenOut, tokenOutDeployment] =
      input.blockchain != null
        ? resolveTokenOut(
            input.blockchain,
            input.tokenIn,
            tokenFamilies,
            LIST_TOKENS_FLATTEN
          )
        : (() => {
            const tokenOut = getBaseTokenInfoWithFallback(input.tokenIn, null)
            return [tokenOut, tokenOut.deployments[0]] as const
          })()

    const blockchain = input.blockchain ?? tokenOutDeployment.chainName
    const initialRecipient = input.recipient ?? ""
    const parsedRecipientResult =
      initialRecipient !== ""
        ? parseRecipient(
            initialRecipient,
            tokenOutDeployment,
            isNearIntentsNetwork(blockchain)
          )
        : null
    const recipient =
      isNearIntentsNetwork(blockchain)
        ? parsedRecipientResult?.kind === "nearcom"
          ? parsedRecipientResult.address.displayAddress
          : initialRecipient
        : parsedRecipientResult != null
          ? initialRecipient
          : ""
    const parsedAmount = input.parsedAmount
      ? {
          amount: BigInt(input.parsedAmount.amount),
          decimals: input.parsedAmount.decimals,
        }
      : null

    return {
      parentRef: input.parentRef,
      tokenIn: input.tokenIn,
      paymentToken: input.paymentToken ?? input.tokenIn,
      paymentTokenCustomized: false,
      tokenOut,
      tokenOutDeployment,
      amount: input.amount ?? "",
      parsedAmount,
      recipient,
      parsedRecipient: parsedRecipientResult,
      destinationMemo: "",
      parsedDestinationMemo: null,
      cexFundsLooseConfirmation: isNearIntentsNetwork(blockchain)
        ? "not_required"
        : cexFundsLooseConfirmationStatusDefault(tokenOutDeployment),
      minReceivedAmount: null,
      blockchain,
    }
  }
)

function fallbackTokenOut(
  token: TokenInfo,
  chainName: string | null
): [BaseTokenInfo, TokenDeployment] {
  const t = getBaseTokenInfoWithFallback(token, chainName)
  return [t, t.deployments[0]]
}

export function getBaseTokenInfoWithFallback(
  tokenIn: TokenInfo,
  chainName: string | null
): BaseTokenInfo {
  if (isBaseToken(tokenIn)) {
    return tokenIn
  }

  if (chainName != null) {
    const tokenOut = tokenIn.groupedTokens.find(
      (token) => token.originChainName === chainName
    )
    if (tokenOut != null) {
      return tokenOut
    }
  }

  const tokenOut = tokenIn.groupedTokens[0]
  assert(tokenOut != null, "Token out not found")
  return tokenOut
}

function parseRecipient(
  recipient: string,
  tokenOut: TokenDeployment,
  isInternalNetwork: boolean
): ParsedWithdrawRecipient | null {
  if (isInternalNetwork) {
    const parsed = parseNearcomAddress(recipient)
    return parsed == null
      ? null
      : {
          kind: "nearcom",
          address: parsed,
        }
  }

  if (tokenOut.chainName === "near") {
    const normalizedRecipient = recipient.toLowerCase()
    return validateAddress(normalizedRecipient, "near")
      ? {
          kind: "destination",
          address: normalizedRecipient,
        }
      : null
  }

  return validateAddress(recipient, tokenOut.chainName)
    ? {
        kind: "destination",
        address: recipient,
      }
    : null
}

export function parseDestinationMemo(
  memo: string,
  chainName: SupportedChainName
): string | null {
  if (chainName !== "xrpledger") return null
  if (memo.trim() === "") return null

  const num = Number(memo.trim())
  if (!Number.isInteger(num) || num < 0 || num > 4294967295) return null

  return num.toString()
}

function cexFundsLooseConfirmationStatusDefault(
  depl: TokenDeployment
): CexFundsLooseConfirmationStatus {
  if (depl.chainName === "hyperliquid") return "not_required"
  return requiresExchangeFundsAck(depl) ? "not_confirmed" : "not_required"
}
